// packages/charts/src/table/presentationKeys.ts
//
// The spec keys that describe how a table is SHOWN, not what it queries.
// They are set on the tile (the column headers in edit mode, the block
// editor) and must win from any spec a snapshot baked earlier. One list, in
// the package, since 2026-09-13 (plan one-table-model, B1b): the dashboard
// cache overlays with it, the report canvas overlays a draft with it, and
// the drift test against the backend's `PRESENTATION_SPEC_KEYS`
// (`services/composition/chart_specs.py`) reads this one constant.

export const PRESENTATION_SPEC_KEYS = [
  "column_formats",
  "column_picker",
  "default_visible_keys",
  "comparison_display",
  // "Break down by" (plan 2026-09-07, D-B1): set on the tile by the block
  // editor, so the snapshot the backend baked before it knows nothing of it.
  "table_breakdown_key",
  // Sorted by the change of one metric (plan 2026-09-13-report-builder-parity-
  // workstreams, W3.2): part delta, a column and an order. A dashboard tile
  // opens with it as its sort; a report table arrives already ordered. (No
  // quoted words in this list's comments: the backend drift test reads every
  // quoted token between the brackets as a key.)
  "table_sort",
  // The colour rules (ADR-0351): written by their own route, never baked by a
  // refresh, so a rule shows the moment it is saved.
  "conditional_formats",
  // A trend line per row over the last N completed periods (plan 2026-10-16-
  // a-table-row-carries-a-sparkline, D1): set by the block editor, so the
  // snapshot must get it from the stored spec. Its series rides the refresh
  // response, never the spec.
  "trend_column",
  // One metric drawn as a bar beside its column (plan 2026-10-17-a-table-
  // shows-one-metric-as-a-bar-column, D1): set by the block editor, nothing
  // served for it, so the snapshot must get it from the stored spec.
  "bar_column",
  // A scorecard's border (plan 2026-09-25 line block, D5): set by the editor,
  // never baked by a refresh, so the snapshot must get it from the stored spec.
  "border",
  // A block's card background (plan 2026-10-09 dashboard shapes, D3b): set by
  // the editor, never baked by a refresh; absent is the card.
  "surface",
  // A chart's palette and series colours (ADR-0476): written by their own
  // route, never baked by a refresh, so a tile hydrated from a tab snapshot
  // must get them from the stored spec.
  "chart_colors",
] as const;

export type PresentationSpecKey = (typeof PRESENTATION_SPEC_KEYS)[number];

/** The snapshot's spec with the stored spec's presentation keys on top: set
 *  when the stored spec has them, removed when it no longer does. Returns a
 *  new object; neither input is mutated. */
export function overlayPresentationKeys(
  snapshotSpec: Record<string, unknown>,
  storedSpec: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!storedSpec) return snapshotSpec;
  const out = { ...snapshotSpec };
  for (const key of PRESENTATION_SPEC_KEYS) {
    if (storedSpec[key] !== undefined) out[key] = storedSpec[key];
    else delete out[key];
  }
  return out;
}
